// Keep Prestige's existing price elements in sync with the selected Appstle plan.
// All amounts and formatting come from Shopify Liquid, including unit pricing.
class LitterPriceSync extends HTMLElement {
  connectedCallback() {
    this.data = JSON.parse(this.querySelector('script').textContent);
    this.priceList = this.closest('price-list');
    const rerender = this.closest('product-rerender');
    this.form = document.forms[rerender?.getAttribute('observe-form')];
    if (!this.form) return;

    this.scope = this.form.closest('product-rerender') || rerender;
    this.salePrice = this.priceList.querySelector('sale-price');
    this.saleLabel = this.salePrice.querySelector('.sr-only')?.outerHTML || '';
    this.abort = new AbortController();
    this.schedule = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => this.update());
    };
    this.form.addEventListener('change', this.schedule, { signal: this.abort.signal });
    this.form.addEventListener('variant:change', this.schedule, { signal: this.abort.signal });
    this.scope.addEventListener('change', this.schedule, { signal: this.abort.signal });

    // Appstle mounts asynchronously and can update hidden plan values with jQuery.
    // Watch only this product's container; updates below are idempotent.
    this.observer = new MutationObserver(this.schedule);
    this.observer.observe(this.scope, {
      childList: true, subtree: true, attributes: true,
      attributeFilter: ['value', 'checked']
    });
    this.priceList.setAttribute('aria-live', 'polite');
    this.priceList.setAttribute('aria-atomic', 'true');
    this.update();
  }

  disconnectedCallback() {
    this.abort?.abort();
    this.observer?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  update() {
    if (!this.isConnected) return;
    // On single-variant products Appstle can place its radios beside the form.
    // Include those controls only within this product's container.
    const inputs = [
      ...this.form.elements,
      ...Array.from(this.scope.querySelectorAll('[name="selling_plan"]')).filter(
        input => !input.form || input.form === this.form
      )
    ];
    const selected = inputs.find(input =>
      input.name === 'selling_plan' && (input.type !== 'radio' || input.checked)
    );
    const planId = selected?.value || selected?.getAttribute('data-appstle-real-value') || '';
    const price = this.data.plans[planId] || this.data.oneTime;

    this.setHTML(this.salePrice, this.saleLabel + price.price);
    this.salePrice.classList.toggle('text-on-sale', price.onSale);
    this.salePrice.classList.toggle('text-subdued', !price.onSale);

    let compare = this.priceList.querySelector('compare-at-price');
    if (price.onSale) {
      if (!compare) {
        compare = document.createElement('compare-at-price');
        compare.className = this.data.compareClass;
        this.salePrice.after(compare);
      }
      const label = document.createElement('span');
      label.className = 'sr-only';
      label.textContent = this.data.compareLabel;
      this.setHTML(compare, label.outerHTML + price.compare);
    } else {
      compare?.remove();
    }

    const unit = this.priceList.querySelector('unit-price');
    if (unit) this.setHTML(unit, `(${price.unitPrice}/${this.data.unitLabel})`);

    // Change only the unit suffix in Appstle's price labels, preserving its markup.
    for (const element of this.scope.querySelectorAll(
      '.appstle_one_time_unit_price, .appstle_selling_plan_unit_price'
    )) {
      const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
      while (walker.nextNode()) {
        const node = walker.currentNode;
        const text = node.nodeValue.replace(/\/item\b/gi, `/${this.data.unitLabel}`);
        if (text !== node.nodeValue) node.nodeValue = text;
      }
    }
  }

  setHTML(element, html) {
    if (element.innerHTML !== html) element.innerHTML = html;
  }
}

if (!customElements.get('litter-price-sync')) {
  customElements.define('litter-price-sync', LitterPriceSync);
}
